import { useTranslation } from "react-i18next";
import { Button } from "~/components/ui/button";
import { Trash2 } from "lucide-react";

interface AttachmentListProps {
  attachments: string;
  onDelete: (attachmentUrl: string) => void;
  isLoading?: boolean;
}

export function AttachmentList({ attachments, onDelete, isLoading = false }: AttachmentListProps) {
  const { t } = useTranslation();

  if (!attachments || attachments.trim() === "") {
    return null;
  }

  // Split attachments by comma and filter out empty strings
  const attachmentUrls = attachments
    .split(",")
    .map((url) => url.trim())
    .filter((url) => url.length > 0);

  if (attachmentUrls.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2">
      <h4 className="text-sm font-medium text-gray-700">{t("Hochgeladene Anhänge")}</h4>
      <div className="space-y-2">
        {attachmentUrls.map((url, index) => (
          <AttachmentBlock
            key={url}
            url={url}
            index={index}
            button={
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onDelete(url)}
                disabled={isLoading}
                className="text-red-600 hover:bg-red-50 hover:text-red-800"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            }
          />
        ))}
      </div>
    </div>
  );
}

export function AttachmentBlock({ url, index, button }: { url: string; index: number; button: React.ReactNode }) {
  const { t } = useTranslation();

  const getFileName = (url: string) => {
    // Extract filename from URL
    const urlParts = url.split("/");
    const fileName = urlParts[urlParts.length - 1];
    // Remove timestamp prefix if present (format: YYYYMMDD_HHMMSS_filename.pdf)
    return fileName?.replace(/^\d{8}_\d{6}_/, "");
  };

  return (
    <div key={url} className="flex items-center justify-between gap-4 rounded-md border border-gray-200 bg-gray-50 p-3">
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-gray-600">
          {t("Anhang")} {index + 1}:
        </span>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 hover:underline"
        >
          {getFileName(url)}
        </a>
      </div>
      {button}
    </div>
  );
}
